import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PersistenciaService } from '../persistencia-movimientos/persistencia.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { AuditService } from '../audit/audit.service';

export interface FieldConfig {
  title: string;
  key: string;
  type: 'text' | 'date' | 'select' | 'number';
  format: string;
  options?: { code: string; description: string }[];
  isEditing?: boolean;
}

export interface SectionConfig {
  title: string;
  fields: FieldConfig[];
}

export interface CodeDescription {
  code: string;
  description: string;
}

// Define una interfaz para los formatos de fecha esperados
interface DateFormat {
  readonly length: number;
  readonly pattern: RegExp;
  readonly toIsoPattern: string; // Nuevo: almacena el patrón de salida
}

// Mapea los formatos de entrada y salida a sus propiedades
const formats: Record<string, DateFormat> = {
  'YYYY-MM-DD': { length: 10, pattern: /^(\d{4})-(\d{2})-(\d{2})$/, toIsoPattern: '$1-$2-$3' },
  'YYYYMMDD': { length: 8, pattern: /^(\d{4})(\d{2})(\d{2})$/, toIsoPattern: '$1$2$3' },
  'DD-MM-YYYY': { length: 10, pattern: /^(\d{2})-(\d{2})-(\d{4})$/, toIsoPattern: '$3-$2-$1' },
  'DDMMYYYY': { length: 8, pattern: /^(\d{2})(\d{2})(\d{4})$/, toIsoPattern: '$3$2$1' },
};

@Injectable({
  providedIn: 'root'
})
export class DoublePreviewService {
  constructor(
    private persistenciaService: PersistenciaService,
    private localStorageService: LocalStorageService,
    private formBuilder: FormBuilder,
    private auditService: AuditService
  ) { }

  /**
   * Construye las secciones de la vista a partir de la configuración del CSV.
   * @param csvConfig La configuración de los campos del CSV.
   * @returns Un array de objetos SectionConfig.
   */
  public getDetailSections(csvConfig: any[]): SectionConfig[] {
    const sectionsMap = new Map<string, FieldConfig[]>();

    console.log('csvConfig', csvConfig);

    const sortedFields = csvConfig
      .filter(row => row.visible === 'x')
      .sort((a, b) => {
        const sectionOrderA = parseFloat(a.order.split('.')[0]);
        const sectionOrderB = parseFloat(b.order.split('.')[0]);
        if (sectionOrderA !== sectionOrderB) {
          return sectionOrderA - sectionOrderB;
        }
        const fieldOrderA = parseFloat(a.order.split('.')[1]);
        const fieldOrderB = parseFloat(b.order.split('.')[1]);
        return fieldOrderA - fieldOrderB;
      });

    console.log('sortedFields', sortedFields);

    const sectionTitles = sortedFields.map(row => row.section).filter((value, index, self) => self.indexOf(value) === index);

    sortedFields.forEach(row => {
      const sectionTitle = row.section;
      const field: FieldConfig = {
        title: row.title,
        key: row.key.trim(),
        type: 'text',
        isEditing: row.editable === 'x',
        options: row.options,
        format: null
      };

      if (row.typeView === 'select') {
        field.type = 'select';
      } else if (row.typeView === 'date') {
        field.type = 'date';
        field.format = row.format;
      } else if (row.typeView === 'number') {
        field.type = 'number';
      }

      if (!sectionsMap.has(sectionTitle)) {
        sectionsMap.set(sectionTitle, []);
      }
      sectionsMap.get(sectionTitle).push(field);
    });

    console.log('sortedFields2', sortedFields);

    return sectionTitles.map(title => ({
      title,
      fields: sectionsMap.get(title) || []
    }));
  }

  /**
   * Construye un FormGroup para el detalle, formateando las fechas para el datepicker.
   * @param data El objeto de datos inicial.
   * @param csvConfig La configuración de los campos del CSV.
   */
  public buildForm(data: any, csvConfig: any[]): FormGroup {
    const formGroupConfig: { [key: string]: any } = {};
    const values = { ...data };

    csvConfig.forEach(row => {
      const fieldKey = row.key.trim();
      if (values.hasOwnProperty(fieldKey)) {
        let initialValue = values[fieldKey];
        if (row.typeView === 'date') {
          initialValue = this.formatToIsoDate(initialValue, row.format);
        }
        formGroupConfig[fieldKey] = [initialValue];
      }
    });
    console.log('DATA formGroupConfig', formGroupConfig);
    return this.formBuilder.group(formGroupConfig);
  }

  /**
   * Prepara los valores del formulario para enviarlos al servicio DIS, revirtiendo el formato de fecha.
   * @param formValues Los valores del formulario.
   * @param csvConfig La configuración de los campos del CSV.
   */
  public preparePayloadData(formValues: any, csvConfig: any[]): any {
    const valuesToSave = { ...formValues };
    const dateFields = csvConfig.filter(f => f.typeView === 'date');

    console.log('&&& dateFields', dateFields);
    dateFields.forEach(field => {
      const fieldKey = field.key.trim();
      console.log('&&& valuesToSave[fieldKey]', field, valuesToSave[fieldKey]);
      if (valuesToSave[fieldKey] && typeof valuesToSave[fieldKey] === 'string') {
        // valuesToSave[fieldKey] = valuesToSave[fieldKey].replace(/-/g, '');
        valuesToSave[fieldKey] = this.formatFromIsoDate(valuesToSave[fieldKey], field.format);
        console.log('&&& valuesToSave', valuesToSave);
      }
    });

    return valuesToSave;
  }

  /**
   * Construye el objeto de auditoría a partir de los cambios del formulario.
   * Delegado al AuditService centralizado.
   * @param originalObject El objeto original.
   * @param form El formulario con los cambios.
   */
  public buildAuditObject(originalObject: any, form: FormGroup): any {
    return this.auditService.buildAuditForDoublePreview(originalObject, form);
  }

  /**
   * Crea el payload del DIS completo.
   * @param dataToSave Datos del formulario.
   * @param dataview La definición del dataview.
   * @param csvConfig La configuración del CSV.
   * @param originalObject El objeto original.
   * @param form El formulario.
   */
  public createDISPayload(dataToSave: any, dataview: any, csvConfig: any[], originalObject: any, form: FormGroup): any {
    const formattedData = this.dataToSaveDISfromDataview(dataToSave, dataview, csvConfig);
    const auditData = this.buildAuditObject(originalObject, form);
    return { data: formattedData, audit: auditData };
  }

  // --- MÉTODOS HELPERS (MOVIDOS DEL COMPONENTE Y SERVICIO ORIGINAL) ---
  public dataToSaveDISfromDataview(valuesForm: any, dataviews: any, csvConfig: any[]): any {
    const valuesChange = { ...valuesForm };
    const data = [];

    if (csvConfig) {
      csvConfig.forEach(config => {
        if (config.typeView === 'select' && config.associatedField) {
          const selectedCode = valuesChange[config.key];
          if (selectedCode) {
            const selectedOption = config.options.find(opt => opt.code === selectedCode);
            if (selectedOption) {
              valuesChange[config.associatedField] = selectedOption.description;
            }
          }
        }
      });
    }

    const salidaStr = this.buildDISString(valuesChange, dataviews);
    data.push(salidaStr);
    return data;
  }

  public dataToSaveDISfromDataviewPreview(valuesChange: any, dataviews: any): any {
    const salidaStr = valuesChange.data.map(
      jsonString => {
        return this.buildDISString(JSON.parse(jsonString), dataviews)
      }
    );
    return salidaStr;
  }

  private buildDISString(valuesChange: any, dataviews: any): string {
    let salidaStr = "{";
    if (dataviews) {
      const keys = Object.keys(dataviews);
      keys.forEach((key, i) => {
        const element = dataviews[key];
        let point = keys.length === i + 1 ? "" : ",";
        if (element.name === "keying") {
          salidaStr += `"${element.name}": []${point}`;
        } else {
          let valueCapture = valuesChange[element.name];
          salidaStr += this.formatoValue(element, point, valueCapture);
        }
      });
    }
    salidaStr += "}";
    return salidaStr;
  }

  private formatoValue(element: any, point: string, valueCapture: any): string {
    let salidaStr = '';
    const isNullable = Array.isArray(element.type) && element.type.includes('null');
    const typeObj = isNullable ? element.type.find(t => t !== 'null') : element.type;
    const type = this.optionType(typeObj);

    if (type === 'array') {
      const fieldDefinitions = typeObj.items?.fields || [];
      const valueConvert = this.createObjectDISArray(valueCapture, fieldDefinitions);
      salidaStr += `"${element.name}":${JSON.stringify(valueConvert)}${point}`;
    } else {
      const valueConvert = this.convertFormatDIS(type, valueCapture);
      if (isNullable) {
        salidaStr += valueConvert !== null
          ? `"${element.name}":{"${type}":${valueConvert}}${point}`
          : `"${element.name}":${valueConvert}${point}`;
      } else {
        salidaStr += `"${element.name}":${valueConvert}${point}`;
      }
    }
    return salidaStr;
  }

  private optionType(val: any): string {
    if (typeof val === 'object' && val.type) {
      return val.type;
    }
    return val;
  }

  private createObjectDISArray(value: any[], fieldDefinitions: any[]): any[] {
    if (!Array.isArray(value)) return value;
    return value.map(dataItem => {
      const newObj = {};
      Object.keys(dataItem).forEach(key => {
        const fieldSchema = fieldDefinitions.find(f => f.name === key);
        if (fieldSchema) {
          const type = this.optionType(fieldSchema.type);
          if (fieldSchema.nullable) {
            newObj[key] = { [type]: dataItem[key] };
          } else {
            newObj[key] = dataItem[key];
          }
        }
      });
      return newObj;
    });
  }

  private convertFormatDIS(typeFormat: string, value: any): any {
    if (value === undefined || value === null || value === '') return null;
    switch (typeFormat.toLowerCase().trim()) {
      case 'string':
      case 'char':
      case 'caracter':
        return `"${String(value)}"`;
      case 'int':
      case 'integer':
      case 'number':
      case 'numerico':
      case 'numérico':
        return parseInt(value);
      case 'long':
        return BigInt(value);
      case 'float':
      case 'double':
        return parseFloat(value);
      case 'boolean':
        return String(value).toLowerCase() === 'true';
      default:
        return `"${String(value)}"`;
    }
  }

  /**
   * Convierte una cadena de fecha a un formato de salida específico.
   * @param dateString La cadena de fecha a formatear, en formato 'YYYY-MM-DD'.
   * @param outputFormat El formato de salida deseado (ej. 'YYYYMMDD').
   * @returns La fecha formateada o null si no es válida.
   */
  public formatFromIsoDate(dateString: string, outputFormat: string): string | null {
    const isoFormatSpec = formats['YYYY-MM-DD'];
    const outputFormatSpec = formats[outputFormat];
    if (!dateString || dateString.length !== isoFormatSpec.length || !outputFormatSpec) {
      return null;
    }
    const match = dateString.match(isoFormatSpec.pattern);
    if (!match) {
      return null;
    }
    const [, year, month, day] = match;
    console.log('DATE outputFormat', outputFormat, year, month, day);
    switch (outputFormat) {
      case 'YYYYMMDD':
        return `${year}${month}${day}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'DDMMYYYY':
        return `${day}${month}${year}`;
      default:
        return null;
    }
  }

  public formatToIsoDate(dateString: string, format: string): string | null {
    const formatSpec = formats[format];
    if (!formatSpec) {
      console.error(`Formato de fecha no soportado: ${format}`);
      return null;
    }
    if (!dateString || dateString.length !== formatSpec.length) {
      return null;
    }
    const match = dateString.match(formatSpec.pattern);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  public formatConsolidationDate(dateString: string): string {
    if (!dateString || dateString.length !== 6) return '--';
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    return `${month}/${year}`;
  }

  // Métodos genéricos añadidos
  public getDescriptiveValue(code: string, options: CodeDescription[]): string {
    if (!code || !options) {
      return '--';
    }
    const option = options.find(opt => opt.code === code);
    return option ? option.description : '--';
  }

  public formatDisplayValue(value: any, field: FieldConfig): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (field.type === 'date' && field.format) {
      // console.log('DATA field', value, field);
      return this.formatToIsoDate(value, field.format) || '--';
    }

    if (field.type === 'select' && field.options) {
      const option = field.options.find(opt => opt.code === value);
      return option ? option.code + ': ' + option.description : '--';
    }

    return value.toString();
  }

}
